import { useState, useEffect, useCallback } from 'react';
import { X, Send, Check, Loader2 } from 'lucide-react';
import { submitLead } from '../config/crmApi';
import { PRODUCTS_LIST } from '../config/products';
import './LeadIntakeModal.css';
import { UI_CONSTANTS } from '../config/constants';
import { INDIAN_STATES } from '../config/indianStates';

// Map product IDs to their emoji icons (matching navigation dropdown)
const PRODUCT_ICONS = {
  'swiss-k12': '\u{1F3EB}',
  'swiss-highered': '\u{1F393}',
  'crew': '\u{1F3E2}',
  'elevate': '\u{1F33F}',
  'intellicare': '\u{1F916}',
  'thrive': '\u{1F4DA}',
  'counseling': '\u{1F497}',
};

// Merge product data with icons
const PRODUCTS = PRODUCTS_LIST.map((product) => ({
  ...product,
  icon: PRODUCT_ICONS[product.id],
}));

const LeadIntakeModal = ({ isOpen, onClose, preselectedProductId = null }) => {
  const [step, setStep] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
    institution: '',
    teachers: '',
    faculty: '',
    managers: '',
    students: '',
    employees: '',
    trainees: '',
    users: '',
    learners: '',
    message: '',
    state: '',
    city: '',
    preferredContact: 'email',
    source: 'website'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const isFormDirty = useCallback(() => {
    if (step < 2 || isSuccess) return false;
    return !!(formData.firstName || formData.lastName || formData.email || formData.phone || formData.institution || formData.message);
  }, [step, isSuccess, formData]);

  const handleSafeClose = useCallback(() => {
    if (isFormDirty()) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  }, [isFormDirty, onClose]);

  // Handle Escape key
  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape' && isOpen) {
      handleSafeClose();
    }
  }, [isOpen, handleSafeClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  // Pre-select product when modal opens with a preselectedProductId
  useEffect(() => {
    if (isOpen && preselectedProductId) {
      setSelectedProduct(preselectedProductId);
      setStep(2);
    }
  }, [isOpen, preselectedProductId]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setStep(1);
        setSelectedProduct(null);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          title: '',
          institution: '',
          teachers: '',
          faculty: '',
          managers: '',
          students: '',
          employees: '',
          trainees: '',
          users: '',
          learners: '',
          message: '',
          state: '',
          city: '',
          preferredContact: 'email',
          source: 'website'
        });
        setErrors({});
        setIsSuccess(false);
      }, 300);
    }
  }, [isOpen]);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone) => {
    return !phone || /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/.test(phone);
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    const product = PRODUCTS.find(p => p.id === selectedProduct);
    
    if (product?.fields.includes('institution') && !formData.institution.trim()) {
      newErrors.institution = 'Institution/Organization name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProductSelect = (productId) => {
    setSelectedProduct(productId);
    setStep(2);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleNext = () => {
    if (step === 2 && validateStep2()) {
      setStep(3);
    } else if (step === 3 && validateStep3()) {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    const product = PRODUCTS.find(p => p.id === selectedProduct);

    // Build notes array with form details
    const notesArray = [];
    if (formData.message) notesArray.push(formData.message);
    if (formData.title) notesArray.push(`Title: ${formData.title}`);
    if (formData.preferredContact) notesArray.push(`Preferred Contact: ${formData.preferredContact}`);

    // Add organization metrics to notes
    const metrics = [];
    if (formData.teachers) metrics.push(`Teachers: ${formData.teachers}`);
    if (formData.faculty) metrics.push(`Faculty: ${formData.faculty}`);
    if (formData.managers) metrics.push(`Managers: ${formData.managers}`);
    if (formData.students) metrics.push(`Students: ${formData.students}`);
    if (formData.employees) metrics.push(`Employees: ${formData.employees}`);
    if (formData.trainees) metrics.push(`Trainees: ${formData.trainees}`);
    if (formData.users) metrics.push(`Users: ${formData.users}`);
    if (formData.learners) metrics.push(`Learners: ${formData.learners}`);
    if (metrics.length > 0) notesArray.push(`Organization Size: ${metrics.join(', ')}`);

    // Map to CRM API format
    const leadData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone || undefined,
      company: formData.institution || undefined,
      institution: formData.institution || undefined,
      product: selectedProduct,
      source: 'website',
      state: formData.state || undefined,
      city: formData.city || undefined,
      notes: notesArray,
      tags: [product?.subtitle, product?.name].filter(Boolean)
    };

    try {
      await submitLead(leadData);
      setIsSuccess(true);

      // Auto close after success
      setTimeout(() => {
        onClose();
      }, 3000);

    } catch (error) {
      console.error('Error submitting lead:', error);
      // Show error but still close for better UX
      setIsSuccess(true);
      setTimeout(() => {
        onClose();
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleSafeClose();
    }
  };

  if (!isOpen) return null;

  const product = PRODUCTS.find(p => p.id === selectedProduct);

  return (
    <div 
      className={`lead-modal-overlay ${isOpen ? 'open' : ''}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="lead-modal-container">
        <button
          className="lead-modal-close"
          onClick={handleSafeClose}
          aria-label="Close modal"
        >
          <X size={24} />
        </button>

        {/* Progress Indicator */}
        <div className="lead-modal-progress">
          <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <span className="step-number">1</span>
            <span className="step-label">Select Interest</span>
          </div>
          <div className="progress-line">
            <div className={`progress-fill ${step > 1 ? 'filled' : ''}`} />
          </div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Your Details</span>
          </div>
          <div className="progress-line">
            <div className={`progress-fill ${step > 2 ? 'filled' : ''}`} />
          </div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
            <span className="step-number">3</span>
            <span className="step-label">Organization</span>
          </div>
        </div>

        {/* Success State */}
        {isSuccess ? (
          <div className="lead-modal-success">
            <div className="success-icon">
              <Check size={48} />
            </div>
            <h2>Thank You!</h2>
            <p>Your inquiry has been submitted successfully. Our team will contact you within 24 hours.</p>
            <div className="success-details">
              <p><strong>Product Interest:</strong> {product?.name}</p>
              <p><strong>Contact:</strong> {formData.email}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Step 1: Product Selection */}
            {step === 1 && (
              <div className="lead-modal-content step-1">
                <div className="modal-header">
                  <h2 id="modal-title">How Can We Help?</h2>
                  <p>Select the solution that interests you most</p>
                </div>
                
                <div className="product-grid">
                  {PRODUCTS.map((prod) => {
                    return (
                      <button
                        key={prod.id}
                        className={`product-card ${selectedProduct === prod.id ? 'selected' : ''}`}
                        onClick={() => handleProductSelect(prod.id)}
                        style={{ '--product-color': prod.color }}
                      >
                        <div className="product-icon">
                          <span role="img" aria-label={prod.name}>{prod.icon}</span>
                        </div>
                        <div className="product-info">
                          <h3>{prod.name}</h3>
                          <span className="product-subtitle">{prod.subtitle}</span>
                          <p>{prod.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 2: Contact Details */}
            {step === 2 && (
              <div className="lead-modal-content step-2">
                <div className="modal-header">
                  <div 
                    className="selected-product-badge"
                    style={{ '--product-color': product?.color }}
                  >
                    {product && <span role="img" aria-label={product.name}>{product.icon}</span>}
                    <span>{product?.name}</span>
                  </div>
                  <h2>Tell Us About Yourself</h2>
                  <p>We'll use this information to get in touch with you</p>
                </div>

                <form className="lead-form" onSubmit={(e) => { e.preventDefault(); handleNext(); }}>
                  <div className="form-row">
                    <div className={`form-group ${errors.firstName ? 'has-error' : ''}`}>
                      <label htmlFor="firstName">First Name *</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="Enter your first name"
                        autoComplete="given-name"
                      />
                      {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                    </div>
                    <div className={`form-group ${errors.lastName ? 'has-error' : ''}`}>
                      <label htmlFor="lastName">Last Name *</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Enter your last name"
                        autoComplete="family-name"
                      />
                      {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                    </div>
                  </div>

                  <div className={`form-group ${errors.email ? 'has-error' : ''}`}>
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      autoComplete="email"
                      required
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                  </div>

                  <div className="form-row">
                    <div className={`form-group ${errors.phone ? 'has-error' : ''}`}>
                      <label htmlFor="phone">Phone Number</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder={UI_CONSTANTS.phonePlaceholder}
                        autoComplete="tel"
                      />
                      {errors.phone && <span className="error-message">{errors.phone}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="title">Job Title</label>
                      <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="e.g. Principal, HR Director"
                        autoComplete="organization-title"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="state">State</label>
                      <select
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                      >
                        <option value="">Select state</option>
                        {INDIAN_STATES.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="city">City</label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Enter your city"
                        autoComplete="address-level2"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Preferred Contact Method</label>
                    <div className="radio-group">
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="preferredContact"
                          value="email"
                          checked={formData.preferredContact === 'email'}
                          onChange={handleInputChange}
                        />
                        <span className="radio-custom" />
                        <span>Email</span>
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="preferredContact"
                          value="phone"
                          checked={formData.preferredContact === 'phone'}
                          onChange={handleInputChange}
                        />
                        <span className="radio-custom" />
                        <span>Phone</span>
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="preferredContact"
                          value="whatsapp"
                          checked={formData.preferredContact === 'whatsapp'}
                          onChange={handleInputChange}
                        />
                        <span className="radio-custom" />
                        <span>WhatsApp</span>
                      </label>
                    </div>
                  </div>
                </form>

                <div className="modal-actions">
                  <button className="btn-back" onClick={handleBack}>
                    Back
                  </button>
                  <button className="btn-next" onClick={handleNext}>
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Organization Details */}
            {step === 3 && (
              <div className="lead-modal-content step-3">
                <div className="modal-header">
                  <div 
                    className="selected-product-badge"
                    style={{ '--product-color': product?.color }}
                  >
                    {product && <span role="img" aria-label={product.name}>{product.icon}</span>}
                    <span>{product?.name}</span>
                  </div>
                  <h2>Organization Details</h2>
                  <p>Help us understand your needs better</p>
                </div>

                <form className="lead-form" onSubmit={(e) => { e.preventDefault(); handleNext(); }}>
                  {product?.fields.includes('institution') && (
                    <div className={`form-group ${errors.institution ? 'has-error' : ''}`}>
                      <label htmlFor="institution">
                        {selectedProduct === 'crew' ? 'Organization Name *' : 
                         selectedProduct === 'elevate' ? 'Institution/Practice Name *' :
                         'Institution Name *'}
                      </label>
                      <input
                        type="text"
                        id="institution"
                        name="institution"
                        value={formData.institution}
                        onChange={handleInputChange}
                        placeholder={
                          selectedProduct === 'crew' ? 'Enter company name' :
                          selectedProduct === 'elevate' ? 'Enter institution or practice name' :
                          'Enter school/college name'
                        }
                        autoComplete="organization"
                      />
                      {errors.institution && <span className="error-message">{errors.institution}</span>}
                    </div>
                  )}

                  <div className="form-row">
                    {product?.fields.includes('teachers') && (
                      <div className="form-group">
                        <label htmlFor="teachers">Number of Teachers</label>
                        <input
                          type="number"
                          id="teachers"
                          name="teachers"
                          value={formData.teachers}
                          onChange={handleInputChange}
                          placeholder="e.g. 50"
                          min="0"
                        />
                      </div>
                    )}

                    {product?.fields.includes('faculty') && (
                      <div className="form-group">
                        <label htmlFor="faculty">Number of Faculty</label>
                        <input
                          type="number"
                          id="faculty"
                          name="faculty"
                          value={formData.faculty}
                          onChange={handleInputChange}
                          placeholder="e.g. 100"
                          min="0"
                        />
                      </div>
                    )}

                    {product?.fields.includes('managers') && (
                      <div className="form-group">
                        <label htmlFor="managers">Number of Managers</label>
                        <input
                          type="number"
                          id="managers"
                          name="managers"
                          value={formData.managers}
                          onChange={handleInputChange}
                          placeholder="e.g. 25"
                          min="0"
                        />
                      </div>
                    )}

                    {product?.fields.includes('students') && (
                      <div className="form-group">
                        <label htmlFor="students">Number of Students</label>
                        <input
                          type="number"
                          id="students"
                          name="students"
                          value={formData.students}
                          onChange={handleInputChange}
                          placeholder="e.g. 1500"
                          min="0"
                        />
                      </div>
                    )}

                    {product?.fields.includes('employees') && (
                      <div className="form-group">
                        <label htmlFor="employees">Number of Employees</label>
                        <input
                          type="number"
                          id="employees"
                          name="employees"
                          value={formData.employees}
                          onChange={handleInputChange}
                          placeholder="e.g. 500"
                          min="0"
                        />
                      </div>
                    )}

                    {product?.fields.includes('trainees') && (
                      <div className="form-group">
                        <label htmlFor="trainees">Expected Trainees</label>
                        <input
                          type="number"
                          id="trainees"
                          name="trainees"
                          value={formData.trainees}
                          onChange={handleInputChange}
                          placeholder="e.g. 20"
                          min="0"
                        />
                      </div>
                    )}

                    {product?.fields.includes('users') && (
                      <div className="form-group">
                        <label htmlFor="users">Expected Users</label>
                        <input
                          type="number"
                          id="users"
                          name="users"
                          value={formData.users}
                          onChange={handleInputChange}
                          placeholder="e.g. 1000"
                          min="0"
                        />
                      </div>
                    )}

                    {product?.fields.includes('learners') && (
                      <div className="form-group">
                        <label htmlFor="learners">Expected Learners</label>
                        <input
                          type="number"
                          id="learners"
                          name="learners"
                          value={formData.learners}
                          onChange={handleInputChange}
                          placeholder="e.g. 100"
                          min="0"
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Additional Information</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us more about your needs, timeline, or any questions you have..."
                      rows={4}
                    />
                  </div>
                </form>

                <div className="modal-actions">
                  <button className="btn-back" onClick={handleBack}>
                    Back
                  </button>
                  <button 
                    className="btn-submit" 
                    onClick={handleNext}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="spin" size={20} />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={20} />
                        Submit Inquiry
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        <div className="modal-footer">
          <p>
            By submitting, you agree to our{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            {' '}and{' '}
            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          </p>
        </div>
      </div>

      {/* Discard Changes Confirmation */}
      {showDiscardConfirm && (
        <div className="lead-discard-overlay" onClick={() => setShowDiscardConfirm(false)}>
          <div className="lead-discard-modal" onClick={(e) => e.stopPropagation()}>
            <p className="lead-discard-title">Unsaved Changes</p>
            <p className="lead-discard-text">You have unsaved form data. Are you sure you want to close?</p>
            <div className="lead-discard-actions">
              <button className="lead-discard-keep" onClick={() => setShowDiscardConfirm(false)}>
                Keep Editing
              </button>
              <button className="lead-discard-close" onClick={() => { setShowDiscardConfirm(false); onClose(); }}>
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadIntakeModal;
